// src/pages/Dashboard.jsx
// v3.0 — Full nav wiring: all feature-flagged routes, grouped sections, admin link
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useFeatureFlags } from '../hooks/useFeatureFlag'
import { getClients, getAFSJobs, resumeJobProgress, getCreditSummary } from '../api/kailux'
import { STAGE_TO_ROUTE, STAGE_LABEL } from '../constants/afsWorkflow'
import {
  LayoutDashboard, Users, ClipboardList, BarChart3, Landmark,
  FolderOpen, Shuffle, TrendingUp, Zap, Inbox, Scale, Calendar,
  CreditCard, Settings as SettingsIcon, ShieldCheck, Folder,
} from 'lucide-react'
import EmptyState from '../components/EmptyState'
import Spinner from '../components/Spinner'
import CollapsibleSidebar from '../components/CollapsibleSidebar'
import ThemeToggle from '../components/ThemeToggle'
import { T, alpha } from '../theme/theme'


import Icon from '../components/Icon'
// ── Nav definition — sections with optional feature flags & admin-only items ──
const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { path:'/dashboard',  icon:LayoutDashboard, label:'Dashboard' },
      { path:'/clients',    icon:Users,           label:'Clients'   },
      { path:'/afs-history',icon:ClipboardList,   label:'AFS History'},
    ],
  },
  {
    label: 'UPLOAD',
    items: [
      { path:'/trial-balance',  icon:BarChart3, label:'Trial Balance'  },
      { path:'/bank-statement', icon:Landmark,  label:'Bank Statement' },
    ],
  },
  {
    label: 'PRACTICE',
    items: [
      { path:'/workpapers',          icon:FolderOpen, label:'Workpapers',    needsClient: true },
      { path:'/consolidations',      icon:Shuffle,    label:'Consolidations',  flag:'consolidations_enabled' },
      { path:'/management-accounts', icon:TrendingUp, label:'Mgmt Accounts',   flag:'management_accounts_enabled' },
      { path:'/batch',               icon:Zap,        label:'Batch Jobs'    },
      { path:'/audit',               icon:ShieldCheck, label:'Audit',          flagOn:'engagement_type_audit_enabled' },
    ],
  },
  {
    label: 'FIRM TOOLS',
    items: [
      { path:'/intake-requests',       icon:Inbox,    label:'Intake Requests', flag:'intake_portal_enabled' },
      { path:'/opening-balance-check', icon:Scale,    label:'Opening Balances' },
      { path:'/compliance',            icon:Calendar, label:'Compliance',       flag:'compliance_calendar_enabled' },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { path:'/billing',  icon:CreditCard,   label:'Billing'     },
      { path:'/settings', icon:SettingsIcon, label:'Settings'    },
      { path:'/admin',    icon:ShieldCheck,  label:'Admin Board', adminOnly:true },
    ],
  },
]

export default function Dashboard() {
  const { firm, user, logout } = useAuth()
  const navigate               = useNavigate()
  const { flags }              = useFeatureFlags()

  const [clients,       setClients]       = useState([])
  const [jobs,          setJobs]          = useState([])
  const [loading,       setLoading]       = useState(true)
  const [modal,         setModal]         = useState(null)
  const [hoveredClient, setHoveredClient] = useState(null)
  const [resuming,    setResuming]    = useState(null)
  const [resumeError, setResumeError] = useState(null)
  const [creditCounts, setCreditCounts] = useState(null)

  useEffect(() => {
    if (!firm?.id) return
    Promise.all([
      getClients(firm.id),
      getAFSJobs(firm.id),
    ]).then(([c, j]) => {
      setClients(c.data || [])
      setJobs(j.data || [])
    }).catch(console.error)
    .finally(() => setLoading(false))
  }, [firm?.id])

  useEffect(() => {
    if (firm?.plan_type !== 'pay_per_engagement') return
    getCreditSummary().then(r => setCreditCounts(r.data?.credits || null)).catch(() => {})
  }, [firm?.plan_type])

  const complete   = jobs.filter(j=>j.status==='complete').length
  // FIX (Phase 5, Issue A): this previously filtered on status values
  // ('mapping','review') that the backend never actually sets — afs_jobs.
  // status is only ever 'draft', 'upload', or 'complete' — so this count
  // was silently always ~0 regardless of how many jobs were genuinely in
  // progress. AFSHistory.jsx's equivalent stat already used the correct
  // definition (status !== 'complete'); this now matches it.
  const inProgress = jobs.filter(j=>j.status!=='complete').length
  const inProgressJobs = jobs.filter(j=>j.status!=='complete')
    .sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0))
    .slice(0,3)

  // Phase 5 (Issue A) — mirrors AFS History's Resume behaviour, surfaced
  // here too so a preparer doesn't have to know to go to AFS History
  // specifically to pick up where they left off.
  const handleResume = async (job) => {
    setResuming(job.id)
    setResumeError(null)
    try {
      const { data } = await resumeJobProgress(job.id)
      const route = STAGE_TO_ROUTE[data.workflow_stage] || '/trial-balance'
      const client = clients.find(c => c.id === job.client_id)
      navigate(route, {
        state: {
          client: { id: data.client_id, name: client?.name || '' },
          afsJobId: data.job_id,
          mappings: data.mappings, accounts: data.accounts, figures: data.figures,
          prior_figures: data.prior_figures, first_year: data.first_year,
          prior_not_available: data.prior_not_available,
          financial_year: data.financial_year, engagement_type: data.engagement_type,
          notes: data.notes_override,
          // Draft-autosave fix: see AFSHistory.jsx's handleResume for why
          // this needs its own field, separate from the trial-balance-
          // shaped ones above.
          bank_statement_data: data.bank_statement_data,
        },
      })
    } catch (err) {
      setResumeError(job.id)
      setTimeout(() => setResumeError(null), 3000)
    } finally {
      setResuming(null)
    }
  }
  // Phase A (25 Jul 2026) — grace-period banner state. Mirrors the backend's
  // is_generation_locked() in main.py: GRACE_DAYS must match on both sides,
  // or the frontend will show a stale countdown relative to when the
  // backend actually locks generation.
  const GRACE_DAYS = 7
  const graceInfo = (() => {
    const now = new Date()
    // Free trial removed (Sep 2026): an 'unpaid' firm has never subscribed,
    // so there is no grace-period countdown — it's simply locked until they
    // subscribe from Billing. Unlike 'expired'/payment-failure below, this
    // isn't a firm that HAD access and lost it.
    if (firm?.subscription_status === 'unpaid') {
      return { locked:true, daysLeft:0, reason:'unpaid' }
    }
    if (firm?.payment_failed_at) {
      const graceEnds = new Date(new Date(firm.payment_failed_at).getTime() + GRACE_DAYS*24*60*60*1000)
      const daysLeft = Math.ceil((graceEnds-now)/(1000*60*60*24))
      return daysLeft > 0
        ? { locked:false, daysLeft, reason:'payment' }
        : { locked:true, daysLeft:0, reason:'payment' }
    }
    return null
  })()

  const openModal  = (destination) => setModal(destination)
  const closeModal = () => { setModal(null); setHoveredClient(null) }

  const selectClient = (client) => {
    const destination = modal
    closeModal()
    navigate(`/${destination}`, { state: { client } })
  }

  const MODAL_META = {
    'trial-balance':  { title: 'Upload Trial Balance',  icon: BarChart3 },
    'bank-statement': { title: 'Upload Bank Statement', icon: Landmark },
    'workpapers':     { title: 'Open Workpapers',       icon: FolderOpen },
  }
  const modalTitle = MODAL_META[modal]?.title || 'Select a client'
  const ModalIcon   = MODAL_META[modal]?.icon  || Folder
  const currentPath = window.location.pathname
  const isAdmin    = user?.app_metadata?.role === 'admin'

  // ── Determine whether a nav item should be shown ──
  const isItemVisible = (item) => {
    if (item.adminOnly && !isAdmin) return false
    if (item.flag && flags[item.flag] === false) return false
    // Closed-by-default features (audit): visible only when the flag is explicitly on, or to a platform admin.
    if (item.flagOn && flags[item.flagOn] !== true && !isAdmin) return false
    return true
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex' }}>
      <style>{`@keyframes kailuxModalIn { from { opacity:0; transform:translateY(14px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>

      {/* ── CLIENT SELECTOR MODAL ─────────────────────────────────────────── */}
      {modal && (
        <div onClick={closeModal}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
            display:'flex', alignItems:'center', justifyContent:'center',
            zIndex:200, padding:'24px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'16px',
              padding:'32px', width:'100%', maxWidth:'460px',
              boxShadow:T.shl,
              animation:'kailuxModalIn 0.25s ease' }}>

            <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px' }}>
              <ModalIcon size={22} strokeWidth={2} color={T.or} />
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
                {modalTitle}
              </div>
            </div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>
              Select a client to continue
            </div>

            {clients.length === 0 ? (
              <EmptyState icon={Users} title="No clients yet"
                subtitle="You need to add a client before uploading documents"
                action={
                  <button onClick={() => { closeModal(); navigate('/clients') }}
                    style={{ padding:'11px 24px', borderRadius:'8px', border:'none',
                      background:T.prg, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                      fontWeight:'700', cursor:'pointer' }}>
                    Add a Client →
                  </button>
                } />
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px',
                maxHeight:'320px', overflowY:'auto' }}>
                {clients.map(client => (
                  <div key={client.id} onClick={() => selectClient(client)}
                    onMouseEnter={() => setHoveredClient(client.id)}
                    onMouseLeave={() => setHoveredClient(null)}
                    style={{ display:'flex', alignItems:'center', gap:'12px',
                      padding:'14px 16px', borderRadius:'12px', cursor:'pointer',
                      border:`1.5px solid ${hoveredClient === client.id ? T.or : T.bd}`,
                      background: hoveredClient === client.id ? T.ord : T.s2,
                      transition:'all 0.15s' }}>
                    <div style={{ width:'38px', height:'38px', borderRadius:'8px',
                      background:T.ord, border:`1px solid ${alpha(T.or, 0.19)}`, flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'15px', fontWeight:'800', color:T.or }}>
                      {client.name[0]}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'14px', fontWeight:'700',
                        color: hoveredClient === client.id ? T.or : T.tx,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {client.name}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                        {client.entity_type} · Year end: {client.year_end_month}
                      </div>
                    </div>
                    <span style={{ fontSize:'16px', color: hoveredClient === client.id ? T.or : T.mt,
                      transition:'color 0.15s' }}>→</span>
                  </div>
                ))}
              </div>
            )}

            <button onClick={closeModal}
              style={{ marginTop:'16px', width:'100%', padding:'12px',
                borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:'transparent', color:T.mt,
                fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── SIDEBAR ───────────────────────────────────────────────────────── */}
      <CollapsibleSidebar width="220px" bg={T.s1} borderColor={T.bd}
        accentFrom={T.or} accentTo={T.or2} mutedColor={T.mt}
        position="sticky" top={0} height="100vh" ariaLabel="main navigation">
      <div style={{ background:T.s1, borderRight:`1px solid ${T.bd}`,
        display:'flex', flexDirection:'column', height:'100vh' }}>

        {/* Brand */}
        <div style={{ padding:'20px 18px 16px', borderBottom:`1px solid ${T.bd}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:'32px', height:'32px', background:T.prg,
              borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Syne',sans-serif", fontWeight:'800', color:T.onpri, fontSize:'14px' }}>K</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'800' }}>
              Kai<span style={{color:T.or}}>Lux</span>
            </div>
          </div>

          {/* Workspace / firm chip — separated from the wordmark with its own
              spacing, background, and avatar so it reads as a distinct "you
              are signed in as..." element rather than a stray caption line. */}
          <div title={firm?.name || 'My Firm'} style={{ display:'flex', alignItems:'center', gap:'8px',
            marginTop:'14px', padding:'8px 10px', borderRadius:'9px',
            background:T.s2, border:`1px solid ${T.bd}` }}>
            <div style={{ width:'22px', height:'22px', flexShrink:0, borderRadius:'6px',
              background:T.dm, border:`1px solid ${T.bd}`, display:'flex', alignItems:'center',
              justifyContent:'center', fontFamily:"'Syne',sans-serif", fontWeight:'700',
              fontSize:'11px', color:T.or }}>
              {(firm?.name || 'M').trim().charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontSize:'8px', fontWeight:'700', color:T.mt, letterSpacing:'0.6px',
                textTransform:'uppercase', lineHeight:1 }}>Workspace</div>
              <div style={{ fontSize:'12.5px', fontWeight:'600', color:T.tx, marginTop:'3px',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {firm?.name || 'My Firm'}
              </div>
            </div>
          </div>
        </div>

        {/* Nav — scrollable so it never clips on small screens */}
        <nav style={{ padding:'8px', flex:1, overflowY:'auto' }}>
          {NAV_SECTIONS.map((section, si) => {
            const visibleItems = section.items.filter(isItemVisible)
            if (visibleItems.length === 0) return null
            return (
              <div key={si} style={{ marginBottom:'4px' }}>
                {section.label && (
                  <div style={{ fontSize:'9px', fontWeight:'700', color:T.mt,
                    letterSpacing:'1.2px', padding:'10px 12px 4px', opacity:0.7 }}>
                    {section.label}
                  </div>
                )}
                {visibleItems.map(item => {
                  const isActive = currentPath === item.path
                  const sharedStyle = {
                    display:'flex', alignItems:'center', gap:'8px',
                    padding:'8px 12px', borderRadius:'8px', textDecoration:'none',
                    color: isActive ? T.or : T.lt, fontSize:'14px',
                    fontWeight: isActive ? '700' : '500',
                    background: isActive ? T.ord : 'transparent',
                    borderLeft: isActive ? `2px solid ${T.or}` : '2px solid transparent',
                    marginBottom:'1px', transition:'all 0.15s', cursor:'pointer'
                  }
                  const inner = (
                    <>
                      <item.icon size={16} strokeWidth={2} style={{ flexShrink:0 }} />
                      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {item.label}
                      </span>
                    </>
                  )
                  if (item.needsClient) {
                    return (
                      <div key={item.path} style={sharedStyle}
                        onClick={() => openModal(item.path.replace('/', ''))}
                        onMouseEnter={e => { if(!isActive){ e.currentTarget.style.background=T.s2; e.currentTarget.style.color=T.tx } }}
                        onMouseLeave={e => { if(!isActive){ e.currentTarget.style.background='transparent'; e.currentTarget.style.color=T.lt } }}>
                        {inner}
                      </div>
                    )
                  }
                  return (
                    <Link key={item.path} to={item.path} style={sharedStyle}
                      onMouseEnter={e => { if(!isActive){ e.currentTarget.style.background=T.s2; e.currentTarget.style.color=T.tx } }}
                      onMouseLeave={e => { if(!isActive){ e.currentTarget.style.background='transparent'; e.currentTarget.style.color=T.lt } }}>
                      {inner}
                    </Link>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* Theme toggle (renders only while previewing the light/dark themes) + Sign out */}
        <div style={{ padding:'14px 18px', borderTop:`1px solid ${T.bd}` }}>
          <ThemeToggle />
          <button onClick={logout}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
              padding:'8px', borderRadius:'8px', border:`1px solid ${alpha(T.rd, 0.3)}`,
              background:T.rdd, color:T.rd, fontSize:'13px', fontWeight:600,
              cursor:'pointer', fontFamily:"'Outfit',sans-serif", transition:'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background=alpha(T.rd, 0.16) }}
            onMouseLeave={e => { e.currentTarget.style.background=T.rdd }}>
            Sign Out
          </button>
        </div>
      </div>
      </CollapsibleSidebar>

      {/* ── MAIN ──────────────────────────────────────────────────────────── */}
      <div style={{ flex:1, overflowY:'auto' }}>

        {/* Top bar */}
        <div style={{ padding:'16px 32px', borderBottom:`1px solid ${T.bd}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          background:T.s1, position:'sticky', top:0, zIndex:20 }}>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
              Dashboard
            </div>
            <div style={{ fontSize:'12.5px', fontWeight:'700', color:T.or,
              display:'flex', alignItems:'center', gap:'6px', marginTop:'3px', letterSpacing:'0.2px' }}>
              <div style={{ width:'5px', height:'5px', borderRadius:'50%',
                background:T.or, boxShadow:`0 0 6px ${T.or}` }}/>
              {new Date().toLocaleDateString('en-ZA',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            {graceInfo && (
              <div style={{ fontSize:'13px', color:graceInfo.locked?T.rd:T.or, fontWeight:'600',
                display:'flex', alignItems:'center', gap:'4px' }}>
                <div style={{ width:'7px', height:'7px', borderRadius:'50%',
                  background:graceInfo.locked?T.rd:T.or,
                  boxShadow:`0 0 6px ${graceInfo.locked?T.rd:T.or}` }}/>
                {graceInfo.locked ? 'AFS generation paused' : `${graceInfo.daysLeft} day${graceInfo.daysLeft===1?'':'s'} to resolve billing`}
              </div>
            )}
            <Link to="/clients">
              <button style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
                background:T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif",
                fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                + New AFS
              </button>
            </Link>
          </div>
        </div>

        <div style={{ padding:'28px 32px' }}>

          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'28px' }}>
            {[
              { label:'Total Clients',   value:clients.length,     color:T.tx },
              firm?.plan_type === 'pay_per_engagement'
                ? { label:'AFS Credits', value:Number(creditCounts?.afs) || 0, color:T.or }
                : { label:'AFS Produced',    value:firm?.afs_count||0, color:T.or },
              { label:'In Progress',     value:inProgress,         color:T.bl },
              { label:'Completed',       value:complete,           color:T.gn },
            ].map((s,i)=>(
              <div key={i} style={{ background:T.s1, border:`1px solid ${T.bd}`,
                borderRadius:'12px', padding:'18px 20px', boxShadow:T.sh }}>
                <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'1.5px',
                  fontWeight:'600', marginBottom:'8px' }}>{s.label.toUpperCase()}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'32px',
                  fontWeight:'800', color:s.color, letterSpacing:'-1px' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Quick actions */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'28px' }}>
            {[
              { icon:BarChart3, title:'Upload Trial Balance',
                desc:'Excel or CSV from any accounting software',
                color:T.gn, action:'trial-balance' },
              { icon:Landmark, title:'Upload Bank Statement',
                desc:'PDF or Excel from any SA bank',
                color:T.or, action:'bank-statement', featured:true },
              { icon:ClipboardList, title:'AFS History',
                desc:'Re-download any previously generated AFS PDF',
                color:T.bl,
                navTo:'/afs-history' },
            ].map((card,i)=>(
              <div key={i}
                onClick={() => {
                  if (card.action) openModal(card.action)
                  else navigate(card.navTo)
                }}
                style={{ background:card.featured?alpha(card.color, 0.03):T.s1,
                  border:`1.5px solid ${card.featured?alpha(card.color, 0.25):T.bd}`,
                  borderRadius:'16px', padding:'24px', cursor:'pointer',
                  transition:'all 0.2s',
                  boxShadow:card.featured?`0 0 24px ${alpha(card.color, 0.07)}`:T.sh }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.borderColor=alpha(card.color, 0.44)}}
                onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.borderColor=card.featured?alpha(card.color, 0.25):T.bd}}>
                <card.icon size={26} strokeWidth={1.75} color={card.color} style={{ marginBottom:'14px' }} />
                <div style={{ fontSize:'14px', fontWeight:'700', marginBottom:'6px',
                  color:card.featured?card.color:T.tx }}>{card.title}</div>
                <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.5' }}>{card.desc}</div>
              </div>
            ))}
          </div>

          {/* Continue where you left off — Phase 5 (Issue A) */}
          {inProgressJobs.length > 0 && (
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
              overflow:'hidden', marginBottom:'20px', boxShadow:T.sh }}>
              <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}`,
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontSize:'14px', fontWeight:'700' }}>Continue where you left off</div>
                <Link to="/afs-history" style={{ fontSize:'13px', color:T.or, textDecoration:'none', fontWeight:'600' }}>
                  View all →
                </Link>
              </div>
              {inProgressJobs.map((job, i) => {
                const client = clients.find(c => c.id === job.client_id)
                const isResuming = resuming === job.id
                const hasResumeError = resumeError === job.id
                return (
                  <div key={job.id} style={{ display:'flex', alignItems:'center',
                    justifyContent:'space-between', padding:'14px 20px',
                    borderBottom:i<inProgressJobs.length-1?`1px solid ${T.bd}`:'none' }}>
                    <div>
                      <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'2px' }}>
                        {client?.name || 'Client'} · {job.financial_year}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt }}>
                        Left at: {STAGE_LABEL[job.workflow_stage] || 'Trial Balance'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleResume(job)}
                      disabled={isResuming}
                      style={{ padding:'7px 16px', borderRadius:'6px', border:'none',
                        background: hasResumeError ? alpha(T.rd, 0.15) : T.bl,
                        opacity: isResuming ? 0.6 : 1,
                        color: hasResumeError ? T.rd : T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700',
                        cursor: isResuming ? 'not-allowed' : 'pointer' }}>
                      {isResuming ? 'Resuming...' : hasResumeError ? <><Icon name="alert" /> Failed — retry</> : '→ Resume'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Recent clients */}
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden', boxShadow:T.sh }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}`,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:'14px', fontWeight:'700' }}>Recent Clients</div>
              <Link to="/clients" style={{ fontSize:'13px', color:T.or, textDecoration:'none', fontWeight:'600' }}>
                View all →
              </Link>
            </div>
            {loading ? (
              <div style={{ padding:'32px', textAlign:'center' }}><Spinner fontSize='14px'/></div>
            ) : clients.length===0 ? (
              <EmptyState icon={Users} title="No clients yet"
                subtitle="Add your first client to start producing financial statements"
                action={
                  <Link to="/clients">
                    <button style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
                      background:T.prg,
                      color:T.onpri, fontFamily:"'Outfit',sans-serif",
                      fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                      Add first client
                    </button>
                  </Link>
                } />
            ) : (
              clients.slice(0,5).map((client,i)=>{
                const clientJobs = jobs.filter(j => j.client_id === client.id && j.status === 'complete')
                const latestJob  = clientJobs.sort((a,b) =>
                  (b.financial_year || '').localeCompare(a.financial_year || ''))[0]

                return (
                  <div key={client.id} style={{ display:'flex', alignItems:'center',
                    justifyContent:'space-between', padding:'14px 20px',
                    borderBottom:i<Math.min(clients.length,5)-1?`1px solid ${T.bd}`:'none' }}>
                    <div>
                      <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'2px' }}>{client.name}</div>
                      <div style={{ fontSize:'12px', color:T.mt }}>
                        {client.entity_type} · Year end: {client.year_end_month}
                        {latestJob && (
                          <span style={{ color:T.gn, marginLeft:'8px' }}>
                            · Last AFS: {latestJob.financial_year}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'4px' }}>
                      {latestJob && (
                        <Link to="/afs-history" style={{ textDecoration:'none' }}>
                          <button style={{ padding:'6px 12px', borderRadius:'6px',
                            border:`1px solid ${T.bd}`, background:'transparent',
                            color:T.mt, fontFamily:"'Outfit',sans-serif",
                            fontSize:'12px', fontWeight:'600', cursor:'pointer',
                            transition:'all 0.2s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor=T.gn; e.currentTarget.style.color=T.gn }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
                            <Icon name="log" /> History
                          </button>
                        </Link>
                      )}
                      <Link to="/trial-balance" state={{ client }} style={{ textDecoration:'none' }}>
                        <button style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                          fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                          New AFS
                        </button>
                      </Link>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Phase A — grace-period / locked notice */}
          {graceInfo && (
            <div style={{ marginTop:'20px', background:graceInfo.locked?T.rdd:T.orb,
              border:`1px solid ${graceInfo.locked ? alpha(T.rd, 0.25) : alpha(T.or, 0.15)}`,
              borderRadius:'12px', padding:'16px 20px', display:'flex',
              alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div style={{ fontSize:'14px', color:T.tx }}>
                {graceInfo.locked ? (
                  <>
                    <strong>AFS generation is currently paused.</strong>{' '}
                    <span style={{ color:T.mt }}>
                      {graceInfo.reason==='unpaid'
                        ? "Your account doesn't have an active subscription yet."
                        : 'Your last payment failed and the grace period has passed.'}{' '}
                      Everything else — logging in, past AFS, and client data — is still fully accessible.
                    </span>
                  </>
                ) : (
                  <>
                    <strong>Your last payment failed.</strong>{' '}
                    <span style={{ color:T.mt }}>
                      You have {graceInfo.daysLeft} day{graceInfo.daysLeft===1?'':'s'} left to
                      update your billing details before
                      AFS generation is paused. Nothing else is affected.
                    </span>
                  </>
                )}
              </div>
              <Link to="/billing">
                <button style={{ padding:'9px 20px', borderRadius:'8px',
                  border:`1px solid ${graceInfo.locked?T.rd:T.or}`,
                  background:'transparent', color:graceInfo.locked?T.rd:T.or,
                  fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                  {graceInfo.reason==='unpaid' ? 'Subscribe now' : 'Update billing'}
                </button>
              </Link>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
